import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Instagram, Tiktok, Youtube } from 'react-bootstrap-icons'
import BrandsSearchBar from './BrandsSearchBar'
import axiosService from '../../../utils/AxiosService'

export default function Brands() {
  const navigate = useNavigate()
  const [brands, setBrands] = useState([])
  const [loading, setLoading] = useState(false)
  const [activeProvider, setActiveProvider] = useState(null) // null = 'all', egyébként a provider neve
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    setLoading(true)

    const params = new URLSearchParams()
    if (activeProvider) {
      params.append('provider', activeProvider)
    }
    if (searchTerm) {
      params.append('search', searchTerm)
    }

    const url = `/api/project_manager/brands${params.toString() ? `?${params.toString()}` : ''}`

    axiosService
      .get(url)
      .then((data) => {
        // Debug: ellenőrizzük, hogy van-e providers mező
        if (data && data.length > 0) {
          console.log('First brand providers:', data[0].providers, 'First brand:', data[0].name)
        }
        setBrands(data || [])
      })
      .catch(() => {
        setBrands([])
      })
      .finally(() => {
        setLoading(false)
      })
  }, [activeProvider, searchTerm])

  const pinnedBrands = useMemo(
    () => [...brands].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 10),
    [brands]
  )

  const allBrands = useMemo(
    () =>
      [...brands].sort((a, b) => {
        const nameA = (a.name || '').toString()
        const nameB = (b.name || '').toString()

        return nameA.localeCompare(nameB)
      }),
    [brands]
  )

  const goToBrand = (id) => () => {
    navigate(`/project_manager/brands/${id}`)
  }

  const handleProviderChange = (provider) => {
    // 'all' esetén null-t állítunk be, hogy ne küldjük el a paramétert
    setActiveProvider(provider === 'all' ? null : provider)
  }

  const handleSearchChange = (term) => {
    setSearchTerm(term)
  }

  // Provider ikonok mapping
  const getProviderIcon = (provider) => {
    const iconMap = {
      instagram: { Icon: Instagram, color: 'text-pink-600' },
      tiktok: { Icon: Tiktok, color: '' },
      youtube: { Icon: Youtube, color: 'text-red-500' },
    }
    return iconMap[provider]
  }

  return (
    <div className="pt-20 px-8 pb-10 bg-rb-bg dark:bg-rb-dark-mode-background min-h-screen">
      <BrandsSearchBar
        activeProvider={activeProvider}
        onProviderChange={handleProviderChange}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
      />

      {/* Pinned brands szekció */}
      <section className="mb-10">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold dark:text-white">Pinned brands · {pinnedBrands.length}</h2>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {pinnedBrands.map((brand) => (
            <button
              key={brand.id}
              onClick={goToBrand(brand.id)}
              className="bg-white dark:bg-rb-dark-mode rounded-xl shadow-sm border border-rb-widget-border dark:border-rb-dark-mode-lighter px-4 py-3 text-left hover:shadow-md hover:border-rb-green dark:hover:border-rb-green transition"
            >
              <div className="flex items-start justify-between gap-3">
                {/* Logo – 3 “sor” magas blokk */}
                {brand.logo_url ? (
                  <img
                    src={brand.logo_url}
                    alt={brand.name || 'Brand logo'}
                    className="h-16 w-16 rounded-md object-cover bg-gray-100 dark:bg-rb-dark-mode-lighter flex-shrink-0 border border-solid border-gray-600 dark:border-rb-dark-mode-lighter"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-md bg-red-100 dark:bg-rb-dark-mode-lighter flex items-center justify-center text-xs font-bold text-red-600 dark:text-gray-400 flex-shrink-0 border border-solid border-gray-600 dark:border-rb-dark-mode-lighter">
                    {(brand.name || 'BR')[0]}
                  </div>
                )}

                <div className="flex-1">
                  <div className="text-sm font-semibold leading-tight dark:text-white">{brand.name}</div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">{brand.domain}</div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {(brand.providers || []).slice(0, 3).map((provider) => {
                        const providerData = getProviderIcon(provider)
                        if (!providerData) {
                          console.warn('No icon found for provider:', provider)
                          return null
                        }
                        const { Icon, color } = providerData
                        return (
                          <span
                            key={provider}
                            className={`h-4 w-4 flex items-center justify-center ${color} ${color === '' ? 'dark:text-white' : ''} ${color === 'text-pink-600' ? 'dark:text-pink-400' : ''} ${color === 'text-red-500' ? 'dark:text-red-400' : ''}`}
                            title={provider}
                          >
                            <Icon className="h-4 w-4" />
                          </span>
                        )
                      })}
                      {(!brand.providers || brand.providers.length === 0) && (
                        <span className="h-4 w-4 rounded-full bg-gray-200 dark:bg-rb-dark-mode-lighter" />
                      )}
                    </div>
                    {brand.posts_count > 0 && (
                      <span className="text-xs text-black dark:text-white">
                        {brand.posts_count} post{brand.posts_count !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>

                {/* Kebab ikon placeholder */}
                <div className="text-gray-400 dark:text-gray-500 text-lg leading-none">⋮</div>
              </div>
              {brand.isYourBrand && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">
                    Your brand
                  </span>
                </div>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* All brands szekció */}
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold dark:text-white">All brands · {allBrands.length}</h2>
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <span>Sort by</span>
            <span className="font-semibold dark:text-white">Name</span>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {allBrands.map((brand) => (
            <button
              key={brand.id}
              onClick={goToBrand(brand.id)}
              className="bg-white dark:bg-rb-dark-mode rounded-xl shadow-sm border border-rb-widget-border dark:border-rb-dark-mode-lighter px-4 py-3 text-left hover:shadow-md hover:border-rb-green dark:hover:border-rb-green transition"
            >
              <div className="flex items-start justify-between gap-3">
                {/* Logo – 3 “sor” magas blokk */}
                {brand.logo_url ? (
                  <img
                    src={brand.logo_url}
                    alt={brand.name || 'Brand logo'}
                    className="h-16 w-16 rounded-md object-cover bg-gray-100 dark:bg-rb-dark-mode-lighter flex-shrink-0 border border-solid border-gray-600 dark:border-rb-dark-mode-lighter"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-md bg-red-100 dark:bg-rb-dark-mode-lighter flex items-center justify-center text-xs font-bold text-red-600 dark:text-gray-400 flex-shrink-0 border border-solid border-gray-600 dark:border-rb-dark-mode-lighter">
                    {(brand.name || 'BR')[0]}
                  </div>
                )}

                <div className="flex-1">
                  <div className="text-sm font-semibold leading-tight dark:text-white">{brand.name}</div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">{brand.domain}</div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {(brand.providers || []).slice(0, 3).map((provider) => {
                        const providerData = getProviderIcon(provider)
                        if (!providerData) {
                          console.warn('No icon found for provider:', provider)
                          return null
                        }
                        const { Icon, color } = providerData
                        return (
                          <span
                            key={provider}
                            className={`h-4 w-4 flex items-center justify-center ${color} ${color === '' ? 'dark:text-white' : ''} ${color === 'text-pink-600' ? 'dark:text-pink-400' : ''} ${color === 'text-red-500' ? 'dark:text-red-400' : ''}`}
                            title={provider}
                          >
                            <Icon className="h-4 w-4" />
                          </span>
                        )
                      })}
                      {(!brand.providers || brand.providers.length === 0) && (
                        <span className="h-4 w-4 rounded-full bg-gray-200 dark:bg-rb-dark-mode-lighter" />
                      )}
                    </div>
                    {brand.posts_count > 0 && (
                      <span className="text-xs text-black dark:text-white">
                        {brand.posts_count} post{brand.posts_count !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>

                {/* Kebab ikon placeholder */}
                <div className="text-gray-400 dark:text-gray-500 text-lg leading-none">⋮</div>
              </div>
            </button>
          ))}
        </div>
      </section>
    </div>
  )
}
